/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// Product block size selector - Add to cart on click
(function() {
  // Create backdrop element for mobile modal
  let backdrop = null;
  let currentOpenModal = null;
  
  // Function to initialize backdrop
  function initBackdrop() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'product-block-sizes-backdrop';
      document.body.appendChild(backdrop);
      
      // Handle backdrop clicks
      backdrop.addEventListener('click', function() {
        closeSizeModal();
      });
    }
  }
  
  // Function to open modal (mobile)
  function openSizeModal(sizesContainer) {
    console.log('Opening size modal');
    initBackdrop();
    currentOpenModal = sizesContainer;
    sizesContainer.classList.add('is-modal-open');
    backdrop.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }
  
  // Function to close modal (mobile)
  function closeSizeModal() {
    console.log('Closing size modal');
    if (currentOpenModal) {
      currentOpenModal.classList.remove('is-modal-open');
    }
    if (backdrop) {
      backdrop.classList.remove('is-active');
    }
    document.body.style.overflow = '';
    currentOpenModal = null;
  }
  
  // Use event delegation for all size selector interactions
  // This works for both initial and dynamically loaded products
  document.addEventListener('click', function(e) {
    // Handle trigger button clicks (mobile)
    const trigger = e.target.closest('.product-block__size-trigger');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      
      const sizesContainer = trigger.nextElementSibling;
      if (sizesContainer && sizesContainer.classList.contains('product-block__sizes')) {
        openSizeModal(sizesContainer);
      }
      return;
    }
    
    // Handle close button clicks
    const closeBtn = e.target.closest('.product-block__sizes-close');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      closeSizeModal();
      return;
    }
    
    // Handle size button clicks
    const sizeButton = e.target.closest('.product-block__size');
    if (sizeButton && !sizeButton.disabled) {
      e.preventDefault();
      e.stopPropagation();
      
      console.log('Size button clicked:', sizeButton.textContent, 'Variant ID:', sizeButton.getAttribute('data-variant-id'));
      
      const variantId = sizeButton.getAttribute('data-variant-id');
      if (!variantId) return;
      
      // Add loading state
      const originalText = sizeButton.textContent;
      sizeButton.textContent = '...';
      sizeButton.disabled = true;
      
      // Add to cart
      console.log('Attempting to add variant to cart:', variantId);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      .then(response => {
        console.log('Cart add response:', response);
        return response.json();
      })
      .then(data => {
        console.log('Cart add success:', data);
        // Show success state
        sizeButton.textContent = '✓';
        sizeButton.classList.add('product-block__size--added');
        
        // Close modal if open (mobile)
        closeSizeModal();
        
        // Refresh the cart drawer contents using Symmetry theme events
        console.log('Dispatching cart-drawer:refresh');
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true
        }));
        
        // Open the cart drawer using Symmetry theme events
        console.log('Dispatching cart-drawer:open');
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        
        // Reset button after delay
        setTimeout(() => {
          sizeButton.textContent = originalText;
          sizeButton.disabled = false;
          sizeButton.classList.remove('product-block__size--added');
        }, 2000);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        sizeButton.textContent = originalText;
        sizeButton.disabled = false;
        alert('Sorry, there was an error adding this item to your cart.');
      });
      return;
    }
  });
  
  // Initialize backdrop on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBackdrop);
  } else {
    initBackdrop();
  }
  
  console.log('Size selector initialized with event delegation');
})();

// ========================================
// PERSONALIZATION / PROPERTIES CODE
// ========================================

const changePreOrderText = (position) => {
  const preOrderField = document.querySelector('#properties-pre-order');
  if (preOrderField != undefined && preOrderField) {
    preOrderField.value = "";
    if (position !== '' && variantPreOrderText[position] !== '' && variantPreOrderText[position] !== undefined) {
      preOrderField.value = variantPreOrderText[position];
    }
  }
}

const backOrderContents = document.querySelectorAll('.js-backorder-copy');
const hideBackToOrderPDP = () => {
  if (backOrderContents.length) {
    backOrderContents.forEach((backOrderContent) => {
      backOrderContent.style.display = "none";
    });
  }
}
const showBackToOrderPDP = (position) => {
  const thisText = document.querySelector('.js-backorder-copy.backorder-copy--' + position);
  if (thisText !== null) {
    thisText.style.display = "block";
  }
}
const showHideAddToCart = (addToCartButton, notifyWhenAvailButton, selectedVariant) => {
  if (addToCartButton !== undefined && addToCartButton !== null && notifyWhenAvailButton !== undefined && notifyWhenAvailButton !== null) {
    console.log(selectedVariant, selectedVariant.classList.contains('is-unavailable'));
    if (selectedVariant.classList.contains('is-unavailable')) {
      addToCartButton.style.display = 'none'
    } else {
      addToCartButton.style.display = 'block'
    }
  }
}
const variantButtons = document.querySelectorAll('.js-variant-button');
const addToCartButton = document.querySelector('.js-add-to-cart-button');
const notifyWhenAvailButton = document.querySelector('.klaviyo-bis-trigger');

if (backOrderContents.length) {
  hideBackToOrderPDP();
}
if (variantButtons.length) {
  variantButtons.forEach((variantButton) => {
    if (variantButton.getAttribute('checked') !== null) {
      showBackToOrderPDP(variantButton.dataset.variantPosition);
      changePreOrderText(variantButton.dataset.variantPosition);
      setTimeout(() => {
        showHideAddToCart(addToCartButton, notifyWhenAvailButton, variantButton);
      }, 1000);
    }
    variantButton.addEventListener('change', (event) => {
      changePreOrderText(event.target.dataset.variantPosition);
      showHideAddToCart(addToCartButton, notifyWhenAvailButton, event.target);
      if (addToCartButton.dataset.preOrder == 1) {
        setTimeout(() => {
          hideBackToOrderPDP();
          showBackToOrderPDP(event.target.dataset.variantPosition);
          if (variantStock[event.target.dataset.variantPosition] <= 0) {
            addToCartButton.innerHTML = theme.strings.products_product_pre_order;
          } else {
            addToCartButton.innerHTML = theme.strings.products_product_add_to_cart;
          }
        }, 100);
      }
    });
  })
}

/**
 * Initialises a button that updates the product form's properties with the relevant availability text.
 * This is currently used for product page 'AddToCart' or 'Add Personalisation' buttons for HOM-3.
 * @param {HTMLElement} button 
 * @param {HTMLElement|null} [form] - Optional, will just use document.querySelector() if no param passed in
 */
const initPropertiesChangeToButton = (button, form) => {

  if (!button) {
    console.warn('No button passed in to initPropertiesChangeToButton')
    return
  }

  const queryElem = form ? form : document

  button.addEventListener("click", () => {
    // Availability section
    const deliveryAvailabilityText = queryElem.querySelector('.js-availability');
    const pickUpAvailabilityText = queryElem.querySelector('.store-availability-information__title');
    const deliveryProp = queryElem.querySelector('.js-prop-delivery-availability');
    const pickupProp = queryElem.querySelector('.js-prop-pickup-availability');
    
    if (deliveryAvailabilityText !== null && deliveryAvailabilityText !== undefined && pickUpAvailabilityText !== null && pickUpAvailabilityText !== undefined) {
      deliveryProp.value = deliveryAvailabilityText.innerText.trim();
      pickupProp.value = pickUpAvailabilityText.innerText.trim();
    }
  });
}

initPropertiesChangeToButton(addToCartButton);
  
// Insurance Add/Remove from Cart
const insuranceCheckbox = document.querySelector("input.shipping-switch-input");

if (insuranceCheckbox) {
    const insurance_id = insuranceCheckbox.value;

    insuranceCheckbox.addEventListener("change", (event) => {
        event.preventDefault(); // ✅ Prevent default behavior
        if (!insurance_id) {
            console.error("Error: Insurance product ID is missing.");
            return;
        }
        // Disable checkbox while fetching
        insuranceCheckbox.disabled = true;

        const url = insuranceCheckbox.checked ? "/cart/add.js" : "/cart/change.js";
        const bodyData = insuranceCheckbox.checked 
            ? { id: insurance_id, quantity: 1 } 
            : { id: insurance_id, quantity: 0 };

        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(bodyData)
        })
        .then(response => response.json())
        .then((data) => {
            // Refresh cart drawer using Symmetry theme events
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                bubbles: true
            }));
        })
        .catch(error => console.error("Error:", error))
        .finally(() => {
            // Re-enable checkbox after request completes
            insuranceCheckbox.disabled = false;
        });
  });
}
